import { Feature, Map as OlMap } from 'ol';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Geometry, MultiPolygon, Polygon } from 'ol/geom';
import { Coordinate } from 'ol/coordinate';
import { Fill, Stroke, Style } from 'ol/style';

export interface HoleCellFeature {
  /** Hole publicGuid */
  id:    string;
  /** Closed exterior ring of each piece of the cell, in the map projection */
  rings: readonly Coordinate[][];
  /** CSS colour of the cell, or null for an outline-only cell */
  color: string | null;
}

const CELL_FILL_ALPHA = 0.55;
const CELL_STROKE     = new Stroke({ color: 'rgba(90, 90, 90, 0.55)', width: 1 });
const EMPTY_FILL      = 'rgba(0, 0, 0, 0)';

/**
 * One Voronoi cell per hole, filled with a per-hole colour. Used by the Geology view
 * (lithology ratio / drill log). The cells themselves come from `computeHoleCells`
 * (shared with the Quality view's powder factor); this layer only draws them.
 */
export class HoleCellsLayer {
  private readonly source = new VectorSource();
  private readonly layer: VectorLayer<VectorSource>;
  private readonly styleCache = new Map<string, Style>();

  constructor(map: OlMap, zIndex: number) {
    this.layer = new VectorLayer({
      source: this.source,
      zIndex,
      style:  feature => this.styleFor(feature.get('color') as string | null),
    });
    map.addLayer(this.layer);
  }

  update(cells: readonly HoleCellFeature[]): void {
    this.source.clear();
    const features: Feature<Geometry>[] = [];
    for (const cell of cells) {
      const rings = cell.rings.filter(ring => ring.length >= 4);
      if (!rings.length) continue;
      const geometry = rings.length === 1
        ? new Polygon([rings[0]])
        : new MultiPolygon(rings.map(ring => [ring]));
      const feature = new Feature(geometry);
      feature.set('holeId', cell.id);
      feature.set('color', cell.color);
      features.push(feature);
    }
    this.source.addFeatures(features);
  }

  clear(): void {
    this.source.clear();
  }

  private styleFor(color: string | null): Style {
    const key = color ?? '';
    let style = this.styleCache.get(key);
    if (!style) {
      style = new Style({
        fill:   new Fill({ color: color ? withAlpha(color, CELL_FILL_ALPHA) : EMPTY_FILL }),
        stroke: CELL_STROKE,
      });
      this.styleCache.set(key, style);
    }
    return style;
  }
}

function withAlpha(color: string, alpha: number): string {
  const hex = color.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!hex) return color;
  let digits = hex[1];
  if (digits.length === 3) digits = digits.split('').map(c => c + c).join('');
  const r = parseInt(digits.slice(0, 2), 16);
  const g = parseInt(digits.slice(2, 4), 16);
  const b = parseInt(digits.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
